import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import { Panel } from './Panel';
import { ColumnDefinition, TABLE_SORT_DIRECTION } from '../../helpers/table/table';
import { SelectOptions } from '../Grid/Grid2';
import { findTwoPanelItemIndex, isSameTwoPanelItem } from './itemIdentity';
import './TwoPanelEditor.scss';

interface Props {
  items: any[];
  itemsCount: number;
  itemsTitle: string;
  itemsColumns: ColumnDefinition[];
  itemsSelected: any[];
  itemCurrentPage: number;

  onItemClick: (index: number, item?: any) => void;

  itemsFilters: any;
  onItemsFilterChange: (event, filterField: string, options?) => void;

  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;
  onItemsFilterClick: (event) => void;

  itemsSetCurrentPageParams: (currentPage: number) => void;

  itemsFiltersOptions?: SelectOptions;
  itemsFilterLabels?: Record<string, string>;

  itemsAccumulator: any[];
  itemsAccumulatorCount: number;
  itemsAccumulatorTitle: string;
  itemsAccumulatorColumns: ColumnDefinition[];
  itemsAccumulatorSelected: any[];
  itemAccumulatorCurrentPage: number;

  onItemAccumulatorClick: (index: number, item?: any) => void;

  itemsAccumulatorFilters: any;
  onItemsAccumulatorFilterChange: (event, filterField: string, options?) => void;

  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;
  onItemsAccumulatorFilterClick: (event) => void;

  itemsAccumulatorFiltersOptions?: SelectOptions;

  onAddClick: () => void;
  onRemoveClick: () => void;

  itemsOnApplyFilterClick: () => void;
  itemsOnClearFilterClick: () => void;

  itemsAccumulatorOnApplyFilterClick: () => void;
  itemsAccumulatorOnClearFilterClick: () => void;

  onSelectAllItemsClick: () => void;
  onSelectAllItemsOnPageClick: () => void;
  onUnselectAllItemsClick: () => void;
  isSelectAllItemsChecked: boolean;

  onSelectAllItemsAccumulatorClick: () => void;
  onSelectAllItemsAccumulatorOnPageClick: () => void;
  onUnselectAllItemsAccumulatorClick: () => void;
  isSelectAllItemsAccumulatorChecked: boolean;

  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;
  onSortItemsClick: (sortField: string) => void;

  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;
  onSortItemsAccumulatorClick: (sortField: string) => void;

  itemsAddBuffer: any[];
  itemsRemoveBuffer: any[];

  onCellItemsClick?: (item) => void;
  onCellItemsAccumulatorClick?: (item) => void;
  removeItemFromBuffer?: (itemId: string) => void;
  onEditClick?: (item: any) => void;
  onAddSingleItem?: (item: any) => void;
  onRemoveSingleItem?: (item: any) => void;
  isItemsLoading?: boolean;
  isItemsAccumulatorLoading?: boolean;
  isSourceFirst?: boolean;
  itemsPresetFilters?: Record<string, string[]>;
  itemsPresetSourceLabel?: string;
  itemsPresetFiltersLocked?: boolean;
  onReplaceItemsFilters?: (filters: any) => void;
  onItemsSearchChange?: (value: string) => void;
  itemsSearchValue?: string;
  onSelectAllFilteredItemsClick?: () => void;
  itemsModernPagination?: boolean;
  itemsAllowSelectAllFiltered?: boolean;
  itemsHiddenFilterChipFields?: string[];
  itemsAccumulatorCapacity?: number;
  clashEventsById?: Record<string, any>;

  // ── Column visibility (items panel) ──
  itemsAllColumns?: ColumnDefinition[];
  itemsHiddenFields?: Set<string>;
  onToggleItemsColumn?: (field: string) => void;
  isItemsColumnToggleable?: (field: string) => boolean;
  onResetItemsColumns?: () => void;
  unitLabel?: string;
  showFilterControl?: boolean;
  showAvatars?: boolean;
  itemsHeaderExtra?: React.ReactNode;
  itemsSearchByNameOnly?: boolean;
  itemsAccumulatorSearchByNameOnly?: boolean;

  // ── Column visibility (accumulator panel) ──
  itemsAccumulatorAllColumns?: ColumnDefinition[];
  itemsAccumulatorHiddenFields?: Set<string>;
  onToggleItemsAccumulatorColumn?: (field: string) => void;
  isItemsAccumulatorColumnToggleable?: (field: string) => boolean;
  onResetItemsAccumulatorColumns?: () => void;
}

type MobileTab = 'accumulator' | 'items';

export const TwoPanelEditor: FunctionComponent<Props> = props => {
  const {
    items,
    itemsTitle,
    itemsColumns,
    itemsAccumulator,
    itemsAccumulatorTitle,
    itemsAccumulatorColumns,
    itemsSelected,
    onItemClick,
    onItemAccumulatorClick,
    itemsAccumulatorSelected,
    onAddClick,
    onRemoveClick,
    onItemsFilterChange,
    itemsFilters,
    itemsFiltersOptions,
    itemsFilterLabels,
    isShowItemsFilter,
    isItemsFiltered,
    onItemsFilterClick,
    itemsAccumulatorFilters,
    isShowItemsAccumulatorFilter,
    isItemsAccumulatorFiltered,
    onItemsAccumulatorFilterClick,
    itemsCount,
    itemsAccumulatorCount,
    itemAccumulatorCurrentPage,
    itemCurrentPage,
    itemsSetCurrentPageParams,
    itemsOnApplyFilterClick,
    itemsOnClearFilterClick,
    itemsAccumulatorOnApplyFilterClick,
    itemsAccumulatorOnClearFilterClick,
    itemsAccumulatorFiltersOptions,
    onItemsAccumulatorFilterChange,
    onSelectAllItemsAccumulatorClick,
    onSelectAllItemsAccumulatorOnPageClick,
    onUnselectAllItemsAccumulatorClick,
    onSelectAllItemsClick,
    onSelectAllItemsOnPageClick,
    onUnselectAllItemsClick,
    isSelectAllItemsChecked,
    isSelectAllItemsAccumulatorChecked,
    sortItemsAccumulatorDirection,
    sortItemsAccumulatorColumnsName,
    onSortItemsAccumulatorClick,
    sortItemsDirection,
    sortItemsColumnsName,
    onSortItemsClick,
    itemsAddBuffer,
    itemsRemoveBuffer,
    onCellItemsClick,
    onCellItemsAccumulatorClick,
    removeItemFromBuffer,
    onEditClick,
    onAddSingleItem,
    onRemoveSingleItem,
    isItemsLoading,
    isItemsAccumulatorLoading,
    isSourceFirst = false,
    itemsPresetFilters,
    itemsPresetSourceLabel,
    itemsPresetFiltersLocked,
    onReplaceItemsFilters,
    onItemsSearchChange,
    itemsSearchValue,
    onSelectAllFilteredItemsClick,
    itemsModernPagination,
    itemsAllowSelectAllFiltered,
    itemsHiddenFilterChipFields,
    itemsAccumulatorCapacity,
    clashEventsById,
    itemsAllColumns,
    itemsHiddenFields,
    onToggleItemsColumn,
    isItemsColumnToggleable,
    onResetItemsColumns,
    itemsAccumulatorAllColumns,
    itemsAccumulatorHiddenFields,
    onToggleItemsAccumulatorColumn,
    isItemsAccumulatorColumnToggleable,
    onResetItemsAccumulatorColumns,
    unitLabel = 'items',
    showFilterControl = true,
    showAvatars = true,
    itemsHeaderExtra,
    itemsSearchByNameOnly = false,
    itemsAccumulatorSearchByNameOnly = false
  } = props;

  const [mobileTab, setMobileTab] = useState<MobileTab>('items');

  const accumulatorPanel = (
    <Panel
      items={itemsAccumulator}
      title={itemsAccumulatorTitle}
      columns={itemsAccumulatorColumns}
      itemsSelected={itemsAccumulatorSelected}
      onItemClick={(index, item) => onItemAccumulatorClick(findTwoPanelItemIndex(itemsAccumulator, item, index), item)}
      filters={itemsAccumulatorFilters}
      isShowFilter={isShowItemsAccumulatorFilter}
      isFiltered={isItemsAccumulatorFiltered}
      onFilterClick={onItemsAccumulatorFilterClick}
      onApplyFilterClick={itemsAccumulatorOnApplyFilterClick}
      onClearFilterClick={itemsAccumulatorOnClearFilterClick}
      filterOptions={itemsAccumulatorFiltersOptions}
      onFilterChange={onItemsAccumulatorFilterChange}
      total={itemsAccumulatorCount}
      currentPage={itemAccumulatorCurrentPage}
      setCurrentPageParams={() => {}}
      paginationLimit={itemsAccumulatorCount}
      onSelectAllClick={onSelectAllItemsAccumulatorClick}
      onSelectAllOnPageClick={onSelectAllItemsAccumulatorOnPageClick}
      onUnselectAllClick={onUnselectAllItemsAccumulatorClick}
      isSelectAllChecked={isSelectAllItemsAccumulatorChecked}
      sortDirection={sortItemsAccumulatorDirection}
      sortColumnsName={sortItemsAccumulatorColumnsName}
      onSortClick={onSortItemsAccumulatorClick}
      itemsAddBuffer={itemsAddBuffer}
      itemsRemoveBuffer={[]}
      onCellClick={onCellItemsAccumulatorClick}
      removeItemFromBuffer={removeItemFromBuffer}
      isNoVisibleBlock={true}
      onEditClick={onEditClick}
      onItemDoubleClick={(index: number, clickedItem?: any) => {
        const resolvedIndex = findTwoPanelItemIndex(itemsAccumulator, clickedItem, index);
        const item = clickedItem || itemsAccumulator[resolvedIndex];
        if (!item || resolvedIndex < 0) return;
        if (onRemoveSingleItem) {
          onRemoveSingleItem(item);
          return;
        }
        const isAlreadySelected = itemsAccumulatorSelected.some(selectedItem => isSameTwoPanelItem(selectedItem, item));
        if (!isAlreadySelected) onItemAccumulatorClick(resolvedIndex, item);
        setTimeout(() => {
          onRemoveClick();
        }, 0);
      }}
      isLoading={isItemsAccumulatorLoading}
      allColumns={itemsAccumulatorAllColumns}
      hiddenFields={itemsAccumulatorHiddenFields}
      onToggleColumn={onToggleItemsAccumulatorColumn}
      isColumnToggleable={isItemsAccumulatorColumnToggleable}
      onResetColumns={onResetItemsAccumulatorColumns}
      isSourceFirst={isSourceFirst}
      unitLabel={unitLabel}
      capacity={itemsAccumulatorCapacity}
      clashEventsById={clashEventsById}
      showFilterControl={showFilterControl}
      showAvatars={showAvatars}
      searchByNameOnly={itemsAccumulatorSearchByNameOnly}
    />
  );

  const itemsPanel = (
    <Panel
      items={items}
      title={itemsTitle}
      columns={itemsColumns}
      itemsSelected={itemsSelected}
      onItemClick={(index, item) => onItemClick(findTwoPanelItemIndex(items, item, index), item)}
      filters={itemsFilters}
      isShowFilter={isShowItemsFilter}
      isFiltered={isItemsFiltered}
      onFilterClick={onItemsFilterClick}
      onApplyFilterClick={itemsOnApplyFilterClick}
      onClearFilterClick={itemsOnClearFilterClick}
      filterOptions={itemsFiltersOptions}
      filterLabels={itemsFilterLabels}
      onFilterChange={onItemsFilterChange}
      total={itemsCount}
      currentPage={itemCurrentPage}
      setCurrentPageParams={itemsSetCurrentPageParams}
      onSelectAllClick={onSelectAllItemsClick}
      onSelectAllOnPageClick={onSelectAllItemsOnPageClick}
      onUnselectAllClick={onUnselectAllItemsClick}
      isSelectAllChecked={isSelectAllItemsChecked}
      sortDirection={sortItemsDirection}
      sortColumnsName={sortItemsColumnsName}
      onSortClick={onSortItemsClick}
      itemsAddBuffer={[]}
      itemsRemoveBuffer={itemsRemoveBuffer}
      onCellClick={onCellItemsClick}
      onEditClick={onEditClick}
      onItemDoubleClick={(index: number, clickedItem?: any) => {
        const resolvedIndex = findTwoPanelItemIndex(items, clickedItem, index);
        const item = clickedItem || items[resolvedIndex];
        if (!item || resolvedIndex < 0) return;
        if (onAddSingleItem) {
          onAddSingleItem(item);
          return;
        }
        const isAlreadySelected = itemsSelected.some(selectedItem => isSameTwoPanelItem(selectedItem, item));
        if (!isAlreadySelected) onItemClick(resolvedIndex, item);
        setTimeout(() => {
          onAddClick();
        }, 0);
      }}
      isLoading={isItemsLoading}
      allColumns={itemsAllColumns}
      hiddenFields={itemsHiddenFields}
      onToggleColumn={onToggleItemsColumn}
      isColumnToggleable={isItemsColumnToggleable}
      onResetColumns={onResetItemsColumns}
      isSourceFirst={isSourceFirst}
      unitLabel={unitLabel}
      presetFilters={itemsPresetFilters}
      presetSourceLabel={itemsPresetSourceLabel}
      lockPresetFilters={itemsPresetFiltersLocked}
      onReplaceFilters={onReplaceItemsFilters}
      onSearchChange={onItemsSearchChange}
      searchValue={itemsSearchValue}
      onSelectAllFilteredClick={onSelectAllFilteredItemsClick}
      modernPagination={itemsModernPagination}
      allowSelectAllFiltered={itemsAllowSelectAllFiltered}
      hiddenFilterChipFields={itemsHiddenFilterChipFields}
      clashEventsById={clashEventsById}
      showFilterControl={showFilterControl}
      showAvatars={showAvatars}
      headerExtra={itemsHeaderExtra}
      searchByNameOnly={itemsSearchByNameOnly}
    />
  );

  const sourceFirstClass = isSourceFirst ? ' twoPanelEditor--sourceFirst' : '';

  return (
    <div className={`twoPanelEditor${sourceFirstClass}`}>
      {/* ── DESKTOP layout (unchanged) ── */}
      <div className="row twoPanelEditor__row twoPanelEditor__desktopLayout">
        <div className="twoPanelEditor__panelCol twoPanelEditor__panelCol--left">
          {isSourceFirst ? itemsPanel : accumulatorPanel}
        </div>

        <div className="twoPanelEditor__actionsCol">
          <div className="twoPanelEditor__actions">
            <button
              type="button"
              onClick={onAddClick}
              className="twoPanelEditor__moveBtn"
              disabled={itemsSelected.length === 0}
              title="Add selected"
              aria-label="Add selected"
            >
              <span aria-hidden="true">{isSourceFirst ? '›' : '‹'}</span>
            </button>
            <button
              type="button"
              onClick={onRemoveClick}
              className="twoPanelEditor__moveBtn"
              disabled={itemsAccumulatorSelected.length === 0}
              title="Remove selected"
              aria-label="Remove selected"
            >
              <span aria-hidden="true">{isSourceFirst ? '‹' : '›'}</span>
            </button>
          </div>
        </div>

        <div className="twoPanelEditor__panelCol twoPanelEditor__panelCol--right">
          {isSourceFirst ? accumulatorPanel : itemsPanel}
        </div>
      </div>

      {/* ── MOBILE layout ── */}
      <div className="twoPanelEditor__mobileLayout">
        {/* Tab bar */}
        <div className="twoPanelEditor__mobileTabs">
          <button
            type="button"
            className={`twoPanelEditor__mobileTab ${mobileTab === 'items' ? 'twoPanelEditor__mobileTab--active' : ''}`}
            onClick={() => setMobileTab('items')}
          >
            {itemsTitle}
          </button>
          <button
            type="button"
            className={`twoPanelEditor__mobileTab ${
              mobileTab === 'accumulator' ? 'twoPanelEditor__mobileTab--active' : ''
            }`}
            onClick={() => setMobileTab('accumulator')}
          >
            {itemsAccumulatorTitle}
            {itemsAccumulatorCount > 0 && (
              <span className="twoPanelEditor__mobileTabBadge">{itemsAccumulatorCount}</span>
            )}
          </button>
        </div>

        {/* Active panel */}
        <div className="twoPanelEditor__mobilePanel">{mobileTab === 'items' ? itemsPanel : accumulatorPanel}</div>
      </div>
    </div>
  );
};

TwoPanelEditor.displayName = 'TwoPanelEditor';
